<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'deletedRecordsHome' }"
          marginBottom="5"/>

      <h6 class="element-header">{{ $t('course.general_title') }}</h6>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('course.private_learning_coaching.deleted_list') }}
        </h5>

        <div class="row align-items-center justify-content-between">
          <PerPage :values="[10, 25, 50, 100]" @update:perPage="getCourses"/>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input name="searchTerm"
                   class="form-control"
                   @input="handleDebounceSearch"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!courses.data?.length"
            icon="fa-trash-can"
            title="Keine gelöschten Einträge gefunden"
            subtitle="Es wurden keine gelöschten Einträge gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>{{ $t('general.designation') }}</th>
              <th>{{ $t('general.operation_type') }}</th>
              <th>{{ $t('general.action_number') }}</th>
              <th>{{ $t('general.validity_start') }}</th>
              <th>{{ $t('general.validity_end') }}</th>
              <th>{{ $t('general.location') }}</th>
              <th>{{ $t('general.payer') }}</th>
              <th>{{ $t('general.units') }}</th>
              <th style=" text-align: right;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(course) in courses.data" :key="course.id">
              <td>{{ course?.name }}</td>
              <td>{{ course?.operationTypeTranslated }}</td>
              <td>{{ course?.actionNumber }}</td>
              <td>{{ course?.startDate }}</td>
              <td>{{ course?.endDate }}</td>
              <td>{{ course?.location?.name }}</td>
              <td>
                <span v-for="(client, index) in course.clients" :key="client.id">
                    {{ client.name }} <span v-if="index < course.clients.length - 1">, </span>&nbsp;
                </span>
              </td>
              <td class="text-end">{{ course?.units }}</td>
              <td class="text-end">
                <button class="btn btn-primary ms-2"
                        @click="undeleteModal(course?.id, course.name)">
                  <i class="os-icon os-icon-arrow-left7"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="courses?.pagination?.total_pages > 1"
            :totalPages="courses?.pagination?.total_pages"
            :currentPage="courses?.pagination?.current_page"
            :totalItems="courses?.pagination?.total_items"
            :perPage="courses?.pagination?.items_per_page"
            @pagechanged="getCourses"/>
      </div>
    </div>


    <ModalConfirm
        v-model="showModal"
        :confirmationQuestion=confirmModalMessage
        :header="''"
        :onConfirm="undeleteCourse"/>

  </PageTemplate>
</template>


<script setup>
import deletedRecordsService from '@/api/DeletedRecords';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import debounce from '@popperjs/core/lib/utils/debounce';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BackButton from "@/components/button/BackButton.vue";

const { t }               = useI18n();
const courses             = ref([]);
const confirmModalMessage = ref('');
const showModal           = ref(false);
const courseId            = ref('');
const isSearching         = ref(false);
const isLoaded            = ref(false);

const filterDebounce = ref({
  searchTerm: '',
});

const debouncedSearch = debounce(async (query) => {
  await getCourses(1, undefined, filterDebounce.value);
}, 600);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getCourses(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = courses.value?.pagination?.items_per_page;

  try {
    const { data } = await deletedRecordsService.getDeletedCoursePrivateLearningCoachingList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : ''),
    });

    courses.value     = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function undeleteModal(id, name) {
  CloseToast();
  courseId.value            = '';
  confirmModalMessage.value = '';
  courseId.value            = id;
  showModal.value           = true;
  confirmModalMessage.value = 'Willst du ' + name + ' wiederherstellen?';
}

async function undeleteCourse() {
  try {
    await deletedRecordsService.undeleteCoursePrivateLearningCoaching(courseId.value);

    courseId.value  = false;
    showModal.value = false;

    await getCourses();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getCourses(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>

</style>